import React, { useContext, useEffect, useMemo, useRef } from "react";
import BryntumContext from "../../../../context/bryntumContext.jsx";
import { BryntumButton } from "@bryntum/schedulerpro-react";
import { canSaveView, capitalizeText, getViewStateIconByRecord, openSwivuAssistPopup, resetSubGrids } from "../../../../../other/utils/utils.jsx";
import { Mask, MessageDialog } from "@bryntum/schedulerpro";
import { EDITOR_KEYS } from "../../../../../other/constants/EditorKeys.jsx";
import useViews from "../../../../hooks/useViews.jsx";
import AppViewStateInspectButton from "../appViewStateInspectButton/AppViewStateInspectButton.jsx";
import { APP_HEADER_HOVER_MENU_BUTTON_CLS, SWIVU_ASSIST_VIEW_TYPE_MAP, SUPPORTED_VIEWS_FOR_EXPORT, TASK_BUDGET_FIELDS, VIEW_TYPES } from "../../../../../other/constants/OtherKeys.jsx";
import { GRANTS } from "../../../../../other/constants/PrivilegeTypes.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../../../other/constants/PersistenceStateKeys.jsx";
import { DEFAULT_THEME } from "../../../../../other/constants/TaskTypes.jsx";
import { exportBoardView } from "../../../../../network/network.jsx";
import { getIsArchivedForBoard } from "../../../../../other/utils/otherUtils.jsx";
import { checkIncentivePermission, handleOutcomeIncentivesHeaderMenu } from "@/other/utils/incentiveUtils.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys.jsx";

const AppSelectedViewButton = () => {
  const buttonRef = useRef(null);
  const { project, editorHandler, user, selectedBoard, viewManager, userPreferences } = useContext(BryntumContext);
  const viewManagerState = useViews();

  const viewMenu = useMemo(() => {
    if (viewManagerState?.selectedView.viewType === VIEW_TYPES.settings) return null;
    return {
    cls: "swivu-header-menu-rounded",
    listeners: {
      beforeShow({ source }) {
        source.getWidgetByRef('saveButton').disabled = !viewManagerState?.selectedView.isOwnView || !viewManagerState.selectedView.hasPIITS;
        const resetBudgetAnnotationButton = source.getWidgetByRef('resetBudgetAnnotationButton');
        resetBudgetAnnotationButton.hidden = viewManagerState?.selectedView.viewType !== VIEW_TYPES.taskBudget;
        resetBudgetAnnotationButton.disabled = !user.can(GRANTS.grant_two);

        /* Show focus mode button only if the view type is task budget & the theme is not dark theme */
        const budgetFocusModeButton = source.getWidgetByRef('budgetFocusModeButton');
        budgetFocusModeButton.hidden = viewManagerState?.selectedView.viewType !== VIEW_TYPES.taskBudget || userPreferences.getSetting("theme") === DEFAULT_THEME;

        budgetFocusModeButton.checked = userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.taskBudgetFocusMode) === "true";

        const swivuAssistToggleButton = source.getWidgetByRef('swivuAssistToggleButton');
        swivuAssistToggleButton.hidden = !SWIVU_ASSIST_VIEW_TYPE_MAP[viewManagerState.selectedView.viewType];

        const exportButton = source.getWidgetByRef('exportView');
        exportButton.hidden = !SUPPORTED_VIEWS_FOR_EXPORT.includes(viewManagerState.selectedView.viewType);
        exportButton.text = `Export ${capitalizeText(viewManagerState.selectedView.viewType)} (CSV)`;

        const incentivesMenu = source.getWidgetByRef('outcomeIncentivesMenu');
        handleOutcomeIncentivesHeaderMenu(incentivesMenu, project, editorHandler, viewManagerState?.selectedView.viewType);

        const pointsLeaderboardButton = source.getWidgetByRef('pointsLeaderboardButton');
        pointsLeaderboardButton.hidden = viewManagerState?.selectedView.viewType !== VIEW_TYPES.incentivePoints;
        pointsLeaderboardButton.disabled = !checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.APPROVED_POINTS_VIEW }) && !checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.AWARDED_POINTS_VIEW });
      }
    },
    itemCls: "swivu-header-menu-item",
    items: [
      {
        ref: "saveButton",
        text: "Save View",
        onItem: () => {
          if (canSaveView(viewManagerState.selectedView.viewType)) {
            const isViewUpdated = viewManager.updateView(
              viewManagerState.selectedView.id,
              viewManagerState.selectedView
            );
            if (isViewUpdated) {
              MessageDialog.alert({
                title: "Success",
                message: "View updated successfully."
              })
            } else {
              MessageDialog.alert({
                title: "Failed",
                message: "Unable to update the view."
              });
            }
          }
        }
      },
      {
        text: "Save View As",
        onItem: () => {
          if (canSaveView(viewManagerState.selectedView.viewType)) {
            editorHandler.openEditor(
              EDITOR_KEYS.viewInspectorEditor,
              null,
              {viewRecord: viewManagerState.selectedView, isSaveAsView: true},
              { by: "recordType", useExisting: true }
            )
          }
        },
      },
      {
        text: "Manage View",
        onItem: () => {
          if (canSaveView(viewManagerState.selectedView.viewType)) {
            editorHandler.openEditor(
              EDITOR_KEYS.viewInspectorEditor,
              null,
              {viewRecord: viewManagerState.selectedView},
              { by: "recordType", useExisting: true }
            )
          }
        }
      },
      {
        ref: "tidyUpButton",
        text: "Tidy Up Columns",
        onItem: () => {
          if (viewManager.pageViewsRef) {
            resetSubGrids(Object.values(viewManager.pageViewsRef))
          }
        }
      },
      {
        ref: "resetBudgetAnnotationButton",
        text: "Reset Budget Annotations",
        onItem: () => {
          project.beginBatch();
          project.taskBudgetStore.forEach((record) => {
            if (!(record.isLinked || record.isGroupHeader) && record[TASK_BUDGET_FIELDS.updatedFields].length) {
              record[TASK_BUDGET_FIELDS.updatedFields] = [];
            }
          })
          project.endBatch();
        }
      },
      {
        ref: "swivuAssistToggleButton",
        text: "Show Swivu Assist",
        hidden: true,
        onItem: () => {
          const swivuAssistanceConfig = SWIVU_ASSIST_VIEW_TYPE_MAP[viewManagerState.selectedView.viewType];
          openSwivuAssistPopup({ swivuAssistanceConfig });
        }
      },
      {
        ref: "budgetFocusModeButton",
        type: "checkbox",
        cls: "show-archive-widget b-menuitem", // These classes are used for styling of this button
        text: "Focus Mode",
        onChange: ({ checked, source }) => {
          source.parent.close();
          userPreferences?.updateSetting(PERSISTENCE_STATE_KEYS.taskBudgetFocusMode, String(checked));
        }
      },
      {
        ref: "exportView",
        onItem: () => {
          Mask.mask({ text: `Exporting ${capitalizeText(viewManagerState.selectedView.viewType)}` })
          const canShowArchived = getIsArchivedForBoard(selectedBoard?.record?.slug);
          exportBoardView(viewManagerState.selectedView.id, canShowArchived, viewManagerState.selectedView.name).then(() => {
            Mask.unmask();
          })
        }
      },
      {
        ref: "outcomeIncentivesMenu",
        text: "Outcome Incentives",
        hidden: true,
        menu: {
          cls: "swivu-header-menu-rounded",
          items: []
        }
      },
      {
        ref: "pointsLeaderboardButton",
        text: "Points Leaderboard",
        onItem: () => {
          editorHandler.openEditor(EDITOR_KEYS.pointsLeaderboardPopup, {}, {});
        }
      }
    ],
  }}, [editorHandler, viewManagerState, viewManager, project, userPreferences, selectedBoard]);

  useEffect(() => {
    if (buttonRef.current && viewManagerState) {
      const menuButton = buttonRef.current.instance;
      const selectedView = viewManagerState.selectedView;
      if (selectedView.color) {
        menuButton.element.style.color = selectedView.color;
      } else {
        menuButton.element.style.removeProperty('color');
      }
    }

  }, [viewManagerState]);

  const canShowButton = Boolean(user) && Boolean(selectedBoard);

  if (canShowButton) {
    return (
      <>
        <BryntumButton
          ref={buttonRef}
          text={viewManagerState?.selectedView.name || "Loading View..."}
          icon={`${getViewStateIconByRecord(viewManagerState?.selectedView, project)}`}
          cls={`swivu-header-menu-button b-transparent ${APP_HEADER_HOVER_MENU_BUTTON_CLS}`}
          iconAlign="end"
          menu={viewMenu}
          menuIcon={null}
        />
        <AppViewStateInspectButton />
      </>
    );
  }
};

export default AppSelectedViewButton;
